import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EVAL_STATUS, POINTS_ALL } from "../../../../constants/index";
import {
  ScoreCard,
  ScoreValue,
  ScoreLabel,
  InfoCard,
  UserAvatar,
  UserInfo,
  UserTitle,
  UserName,
  YearBadge,
  SectionTitle,
  ObjectiveCard,
  ObjectiveHeader,
  ObjectiveNumber,
  ObjectiveWeight,
  CompletionIndicator,
  ObjectiveTitle,
  ObjectiveDescription,
  CompetencyCard,
  CompetencyHeader,
  CompetencyBody,
  CommentBox,
  CommentLabel,
  CommentText,
  ActionButton,
  StatusBadge,
  CollapseIcon,
  ObjectiveSummary,
  ObjectiveMainInfo,
  ObjectiveContentWrapper,
  ObjectiveScoreDisplay,
  ObjectiveExpandedContent,
  ObjectiveAutoEvalButton,
  EvaluationFormContainer,
  FormTitle,
  RatingGrid,
  RatingOption,
  CommentSection,
  CommentLabel2,
  CommentTextarea,
  FormActions,
  SaveButton,
  CancelButton,
  SelfAssessmentDisplay,
  AssessmentHeader,
  AssessmentTitle,
  EditButton,
  AssessmentRating,
  RatingLabel,
  RatingBadge,
  AssessmentComment,
  SkillName,
  InfoIcon,
  SkillTitle,
  SkillWeight,
  SkillSummary,
  EvaluationResultCard,
  EvaluationCommentText,
  EvaluationCommentLabel,
  EvaluationComment,
  EvaluationResultLabel,
  EvaluationResultScore,
  EvaluationResultTitle,
  EvaluationResultIcon,
  EvaluationResultHeader,
  SectionCard,
  SectionHeader,
  SectionTitle2,
  OrgChartHeader,
  BreadcrumbDiv,
} from "./evaluationManagerForm.styled";
import { Tooltip } from "react-tooltip";
import PATHS from "../../../../constants/paths";
import Breadcrumb from "../../../utils/breadCrumbs";

const EvaluationManagerFormView = (props) => {
  const { t } = useTranslation();
  const { data, finishEval, onSaveObjective, user } = props;
  let status;
  const breads = [
    { label: t("home"), path: PATHS.HOME },
    { label: t("forms.evaluation.title"), path: PATHS.EVALUATION },
    { label: t("forms.evaluation.titleEval") },
  ];

  // Estados para colapsar/expandir
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [expandedSkills, setExpandedSkills] = useState({});

  // Estados para formularios de autoevaluación
  const [showObjectiveForm, setShowObjectiveForm] = useState({});
  const [showSkillForm, setShowSkillForm] = useState({});
  const [objectiveRatings, setObjectiveRatings] = useState({});
  const [objectiveComments, setObjectiveComments] = useState({});
  const [skillRatings, setSkillRatings] = useState({});
  const [skillComments, setSkillComments] = useState({});

  const toggleObjective = (index) => {
    setExpandedObjectives((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleSkill = (index) => {
    setExpandedSkills((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    if (data) {
      setObjectiveRatings({
        1: data.point01 || null,
        2: data.point02 || null,
        3: data.point03 || null,
        4: data.point04 || null,
        5: data.point05 || null,
      });

      const skills = {};
      let indexData = 0;
      for (const prof of data.professionalSkill) {
        skills[indexData] = prof.points || null;
        indexData++;
      }
      setSkillRatings(skills);
      if (data.comments && data.comments.length > 0) {
        const autoComments = {};
        const autoSkillComments = {};
        for (const comment of data.comments.filter((c) => c.type === "final")) {
          autoComments[comment.obj] = comment.comment;
        }
        for (const comment of data.comments.filter(
          (c) => c.type === "finalSkill"
        )) {
          autoSkillComments[comment.obj] = comment.comment;
        }
        setSkillComments(autoSkillComments);
        setObjectiveComments(autoComments);
      }
    }
  }, [data]);

  // Funciones para manejar el formulario de autoevaluación de objetivos
  const handleShowObjectiveForm = (index) => {
    setShowObjectiveForm((prev) => ({ ...prev, [index]: true }));
  };

  const handleCancelObjectiveForm = (index) => {
    setShowObjectiveForm((prev) => ({ ...prev, [index]: false }));
  };

  const handleObjectiveRatingChange = (index, rating) => {
    setObjectiveRatings((prev) => ({ ...prev, [index]: rating }));
  };

  const handleObjectiveCommentChange = (index, comment) => {
    setObjectiveComments((prev) => ({ ...prev, [index]: comment }));
  };

  const handleSaveObjectiveAssessment = (index) => {
    // Aquí se simulará el envío - implementación futura
    setShowObjectiveForm((prev) => ({ ...prev, [index]: false }));
    onSaveObjective({
      objectiveIndex: index,
      rating: objectiveRatings[index],
      comment: objectiveComments[index],
      type: "obj",
    });
  };

  const handleSaveSkillAssessment = (index) => {
    // Aquí se simulará el envío - implementación futura

    // console.log("Saving skill assessment:", {
    //   skillIndex: index,
    //   rating: skillRatings[index],
    //   comment: skillComments[index],
    // });
    setShowSkillForm((prev) => ({ ...prev, [index]: false }));

    onSaveObjective({
      objectiveIndex: index,
      rating: skillRatings[index],
      comment: skillComments[index],
      type: "skill",
    });
  };

  // Funciones para manejar el formulario de autoevaluación de competencias
  const handleShowSkillForm = (index) => {
    setShowSkillForm((prev) => ({ ...prev, [index]: true }));
  };

  const handleCancelSkillForm = (index) => {
    setShowSkillForm((prev) => ({ ...prev, [index]: false }));
  };

  const handleSkillRatingChange = (index, rating) => {
    setSkillRatings((prev) => ({ ...prev, [index]: rating }));
  };

  const handleSkillCommentChange = (index, comment) => {
    setSkillComments((prev) => ({ ...prev, [index]: comment }));
  };

  // Opciones de calificación
  const ratingOptions = [
    { code: 5, label: t("rating.A") },
    { code: 4, label: t("rating.BA") },
    { code: 3, label: t("rating.B") },
    { code: 2, label: t("rating.BC") },
    { code: 1, label: t("rating.C") },
    { code: -1, label: t("rating.-") },
  ];

  // Función para obtener los colores según el código de rating
  const getRatingColors = (code) => {
    switch (code) {
      case 5:
        return {
          background: "#10b981", // Verde brillante - Excelente
          border: "#059669",
          text: "#ffffff",
        };
      case 4:
        return {
          background: "#84cc16", // Verde lima - Muy bueno
          border: "#65a30d",
          text: "#ffffff",
        };
      case 3:
        return {
          background: "#f9ae17", // Amarillo corporativo - Cumple
          border: "#d97706",
          text: "#ffffff",
        };
      case 2:
        return {
          background: "#f97316", // Naranja - Por debajo
          border: "#ea580c",
          text: "#ffffff",
        };
      case 1:
        return {
          background: "#ef4444", // Rojo - Muy por debajo
          border: "#dc2626",
          text: "#ffffff",
        };
      case -1:
        return {
          background: "#9ca3af", // Gris neutro - No aplica
          border: "#6b7280",
          text: "#ffffff",
        };
      default:
        return {
          background: "#ffffff",
          border: "#e5e7eb",
          text: "#1f2937",
        };
    }
  };

  // Validar si se puede guardar un objetivo
  const canSaveObjective = (index) => {
    const rating = objectiveRatings[index];
    if (!rating && rating !== -1) return false;
    if (rating === -1) {
      const comment = objectiveComments[index];
      return comment && comment.trim().length > 0;
    }
    return true;
  };

  // Validar si se puede guardar una competencia
  const canSaveSkill = (index) => {
    const rating = skillRatings[index];
    if (!rating && rating !== -1) return false;
    if (rating === -1) {
      const comment = skillComments[index];
      return comment && comment.trim().length > 0;
    }
    return true;
  };

  if (data) {
    status = EVAL_STATUS.find((f) => f.code === data.status);
  }

  const comments = {};

  if (data && data.comments && data.comments.length > 0) {
    for (const comment of data.comments) {
      comments[`${comment.type}${comment.obj}`] = comment.comment;
    }
  }

  const getPoints = (point) => {
    let label;
    if (point !== undefined) {
      const info = POINTS_ALL.find((p) => p.code === point);
      if (info) {
        label = t(info.label);
      }
    } else {
      label = t("point.na");
    }
    return label;
  };

  const getPointsValue = (point) => {
    if (point !== undefined) {
      const info = POINTS_ALL.find((p) => p.code === point);
      return info ? info.code : 0;
    }
    return 0;
  };

  const calculateProgress = (points, weight) => {
    const maxPoints = 5;
    const pointValue = getPointsValue(points);
    return (pointValue / maxPoints) * 100;
  };

  const calculateTotalScore = () => {
    if (!data) return 0;

    let totalScore = 0;
    let totalWeight = 0;

    // Objectives
    const objectives = [
      { points: data.point01, weight: data.weightCriterial01 },
      { points: data.point02, weight: data.weightCriterial02 },
      { points: data.point03, weight: data.weightCriterial03 },
      { points: data.point04, weight: data.weightCriterial04 },
      { points: data.point05, weight: data.weightCriterial05 },
    ];

    objectives.forEach((obj) => {
      if (obj.points !== undefined && obj.weight) {
        const pointValue = getPointsValue(obj.points);
        totalScore += (pointValue * obj.weight) / 5;
        totalWeight += obj.weight;
      }
    });

    // Skills
    if (data.professionalSkill) {
      data.professionalSkill.forEach((skill) => {
        if (skill.points !== undefined && skill.weight) {
          const pointValue = getPointsValue(skill.points);
          totalScore += (pointValue * skill.weight) / 5;
          totalWeight += skill.weight;
        }
      });
    }

    return totalWeight > 0 ? Math.round((totalScore / totalWeight) * 100) : 0;
  };

  const renderComment = (id, isManager = false) => {
    if (!comments[id]) return null;

    return (
      <CommentBox isManager={isManager}>
        <CommentLabel>
          {isManager
            ? t("forms.evaluation.labels.managerComment")
            : t("forms.evaluation.labels.employeeComment")}
          :
        </CommentLabel>
        <CommentText>{comments[id]}</CommentText>
      </CommentBox>
    );
  };

  const renderObjective = (
    index,
    criterial,
    weight,
    autoPoint,
    managerPoint
  ) => {
    if (!criterial) return null;

    const isExpanded = expandedObjectives[index];
    const showTruncated = criterial.length > 0;

    return (
      <ObjectiveCard key={`objective_${index}`}>
        <ObjectiveHeader onClick={() => toggleObjective(index)}>
          <ObjectiveMainInfo>
            <ObjectiveNumber>{index}</ObjectiveNumber>
            <ObjectiveContentWrapper>
              <ObjectiveSummary>
                <ObjectiveTitle>
                  {t("forms.evaluation.labels.objective")} #{index}
                  <ObjectiveWeight>
                    {t("forms.evaluation.labels.weight")}: {weight}%
                  </ObjectiveWeight>
                  <CompletionIndicator
                    $completed={
                      managerPoint !== undefined && managerPoint !== 0
                    }
                  >
                    <i
                      className={`fa-solid ${
                        managerPoint !== undefined && managerPoint !== 0
                          ? "fa-check-circle"
                          : "fa-circle"
                      }`}
                    />
                    {managerPoint !== undefined && managerPoint !== 0
                      ? t("forms.evaluation.labels.completed")
                      : t("forms.evaluation.labels.pending")}
                  </CompletionIndicator>
                </ObjectiveTitle>
              </ObjectiveSummary>
              {!isExpanded && showTruncated && (
                <ObjectiveDescription>
                  {truncateText(criterial, 200)}
                </ObjectiveDescription>
              )}
              {isExpanded && (
                <ObjectiveDescription>{criterial}</ObjectiveDescription>
              )}
            </ObjectiveContentWrapper>
          </ObjectiveMainInfo>

          <ObjectiveScoreDisplay>
            {showTruncated && (
              <CollapseIcon
                className={`fa-solid fa-chevron-down ${
                  isExpanded ? "expanded" : ""
                }`}
              />
            )}
          </ObjectiveScoreDisplay>
        </ObjectiveHeader>

        {isExpanded && (
          <ObjectiveExpandedContent>
            {/* <ProgressBar>
              <ProgressFill progress={calculateProgress(autoPoint, weight)} />
            </ProgressBar> */}
            {/* Mostrar autoevaluación guardada o formulario */}
            {objectiveRatings[index] && !showObjectiveForm[index] ? (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === autoPoint)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`auto${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`auto${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="eval">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="eval">
                      {t("forms.evaluation.labels.managerEvaluation")}
                    </EvaluationResultTitle>
                    <EditButton onClick={() => handleShowObjectiveForm(index)}>
                      <i className="fa-solid fa-pen" />
                      {t("forms.evaluation.buttons.edit")}
                    </EditButton>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="eval">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge>
                        {ratingOptions.find(
                          (r) => r.code === objectiveRatings[index]
                        )?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {objectiveComments[index] && (
                    <EvaluationComment $type="eval">
                      <EvaluationCommentLabel $type="eval">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.managerComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="eval">
                        {objectiveComments[index]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationFormContainer>
              </>
            ) : showObjectiveForm[index] ? (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === autoPoint)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`auto${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`auto${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <FormTitle>
                    {t("forms.evaluation.labels.ratePerformanceEmployee")}
                  </FormTitle>
                  <RatingGrid>
                    {ratingOptions.map((option) => {
                      const colors = getRatingColors(option.code);
                      return (
                        <RatingOption
                          key={option.code}
                          $selected={objectiveRatings[index] === option.code}
                          $bgColor={colors.background}
                          $borderColor={colors.border}
                          $textColor={colors.text}
                          onClick={() =>
                            handleObjectiveRatingChange(index, option.code)
                          }
                        >
                          {option.label}
                        </RatingOption>
                      );
                    })}
                  </RatingGrid>
                  <CommentSection>
                    <CommentLabel2>
                      {t("forms.evaluation.labels.managerComment")}
                      {objectiveRatings[index] === -1 ? (
                        <span style={{ color: "#E03A85", marginLeft: "4px" }}>
                          *
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            marginLeft: "8px",
                            fontWeight: "400",
                          }}
                        >
                          ({t("forms.evaluation.labels.optional")})
                        </span>
                      )}
                    </CommentLabel2>
                    <CommentTextarea
                      placeholder={t(
                        "forms.evaluation.labels.managerPlaceholder"
                      )}
                      value={objectiveComments[index] || ""}
                      onChange={(e) =>
                        handleObjectiveCommentChange(index, e.target.value)
                      }
                    />
                  </CommentSection>
                  <FormActions>
                    <SaveButton
                      className="btn-primary"
                      onClick={() => handleSaveObjectiveAssessment(index)}
                      disabled={!canSaveObjective(index)}
                    >
                      <i className="fa-solid fa-check-circle" />
                      {t("forms.evaluation.buttons.saveAssessment")}
                    </SaveButton>
                    <CancelButton
                      onClick={() => handleCancelObjectiveForm(index)}
                    >
                      <i className="fa-solid fa-times" />
                      {t("forms.evaluation.buttons.cancel")}
                    </CancelButton>
                  </FormActions>
                </EvaluationFormContainer>
              </>
            ) : (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === autoPoint)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`auto${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`auto${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="eval">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="eval">
                      {t("forms.evaluation.labels.managerEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <ObjectiveAutoEvalButton
                    onClick={() => handleShowObjectiveForm(index)}
                  >
                    <i className="fa-solid fa-pen-to-square" />
                    {t("forms.evaluation.buttons.evaluate")}
                  </ObjectiveAutoEvalButton>
                </EvaluationFormContainer>
              </>
            )}
          </ObjectiveExpandedContent>
        )}
      </ObjectiveCard>
    );
  };

  const renderCompetency = (skill, index) => {
    const isExpanded = expandedSkills[index];
    const description = t(`skill:skill_${index + 1}.description`);
    const showTruncated = description.length > 0;

    return (
      <CompetencyCard key={`skill_${index}`} className="mb-3">
        <CompetencyHeader onClick={() => toggleSkill(index)}>
          <div className="row align-items-center">
            <div className="col-auto">
              <ObjectiveNumber>{index + 1}</ObjectiveNumber>
            </div>
            <div className="col">
              <SkillWeight>
                {t("forms.evaluation.labels.weight")}: {skill.weight}%
              </SkillWeight>
              <CompletionIndicator
                $completed={skill.points !== undefined && skill.points !== 0}
              >
                <i
                  className={`fa-solid ${
                    skill.points !== undefined && skill.points !== 0
                      ? "fa-check-circle"
                      : "fa-circle"
                  }`}
                />
                {skill.points !== undefined && skill.points !== 0
                  ? t("forms.evaluation.labels.completed")
                  : t("forms.evaluation.labels.pending")}
              </CompletionIndicator>

              <SkillSummary className="mt-2">
                <SkillTitle>{skill.name}</SkillTitle>
                <SkillName>
                  <InfoIcon
                    className="fas fa-info-circle"
                    data-tooltip-id={`tooltip-skill-${index}`}
                  />
                </SkillName>
                <Tooltip id={`tooltip-skill-${index}`}>
                  <div style={{ maxWidth: "300px" }}>{description}</div>
                </Tooltip>

                {/* <ObjectiveDescription>
                  {isExpanded || !showTruncated
                    ? description
                    : truncateText(description, 200)}
                </ObjectiveDescription> */}
              </SkillSummary>
            </div>
            {showTruncated && (
              <div className="col-auto">
                <CollapseIcon
                  className={`fa-solid fa-chevron-down ${
                    isExpanded ? "expanded" : ""
                  }`}
                />
              </div>
            )}
          </div>
        </CompetencyHeader>

        {isExpanded && (
          <ObjectiveExpandedContent>
            {/* Mostrar autoevaluación guardada o formulario */}
            {skillRatings[index] && !showSkillForm[index] ? (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === skill.autoPoints)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`autoSkill${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`autoSkill${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="eval">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="eval">
                      {t("forms.evaluation.labels.managerEvaluation")}
                    </EvaluationResultTitle>
                    {/* {data?.autoEvalAllowed && ( */}
                    <EditButton onClick={() => handleShowSkillForm(index)}>
                      <i className="fa-solid fa-pen" />
                      {t("forms.evaluation.buttons.edit")}
                    </EditButton>
                    {/* )} */}
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="eval">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge>
                        {ratingOptions.find(
                          (r) => r.code === skillRatings[index]
                        )?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {skillComments[index] && (
                    <EvaluationComment $type="eval">
                      <EvaluationCommentLabel $type="eval">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.managerComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="eval">
                        {skillComments[index]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationFormContainer>
              </>
            ) : showSkillForm[index] ? (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === skill.autoPoints)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`autoSkill${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`autoSkill${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <FormTitle>
                    {t("forms.evaluation.labels.ratePerformanceEmployee")}
                  </FormTitle>

                  <RatingGrid>
                    {ratingOptions.map((option) => {
                      const colors = getRatingColors(option.code);
                      return (
                        <RatingOption
                          key={option.code}
                          $selected={skillRatings[index] === option.code}
                          $bgColor={colors.background}
                          $borderColor={colors.border}
                          $textColor={colors.text}
                          onClick={() =>
                            handleSkillRatingChange(index, option.code)
                          }
                        >
                          {option.label}
                        </RatingOption>
                      );
                    })}
                  </RatingGrid>

                  <CommentSection>
                    <CommentLabel2>
                      {t("forms.evaluation.labels.managerComment")}
                      {skillRatings[index] === -1 ? (
                        <span style={{ color: "#E03A85", marginLeft: "4px" }}>
                          *
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            marginLeft: "8px",
                            fontWeight: "400",
                          }}
                        >
                          ({t("forms.evaluation.labels.optional")})
                        </span>
                      )}
                    </CommentLabel2>
                    <CommentTextarea
                      placeholder={t(
                        "forms.evaluation.labels.managerPlaceholder"
                      )}
                      value={skillComments[index] || ""}
                      onChange={(e) =>
                        handleSkillCommentChange(index, e.target.value)
                      }
                    />
                  </CommentSection>

                  <FormActions>
                    <SaveButton
                      className="btn-primary"
                      onClick={() => handleSaveSkillAssessment(index)}
                      disabled={!canSaveSkill(index)}
                    >
                      <i className="fa-solid fa-check-circle" />
                      {t("forms.evaluation.buttons.saveAssessment")}
                    </SaveButton>
                    <CancelButton onClick={() => handleCancelSkillForm(index)}>
                      <i className="fa-solid fa-times" />
                      {t("forms.evaluation.buttons.cancel")}
                    </CancelButton>
                  </FormActions>
                </EvaluationFormContainer>
              </>
            ) : (
              <>
                <EvaluationResultCard $type="auto">
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="auto">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="auto">
                      {t("forms.evaluation.labels.selfEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <div className="d-flex align-items-center">
                    <EvaluationResultLabel $type="auto">
                      {t("forms.evaluation.labels.employeeScore")}
                    </EvaluationResultLabel>
                    <AssessmentRating>
                      <RatingBadge $type="auto">
                        {ratingOptions.find((r) => r.code === skill.autoPoints)
                          ?.label ||
                          t("forms.evaluation.labels.outAutoEvaluation")}
                      </RatingBadge>
                    </AssessmentRating>
                  </div>
                  {comments[`autoSkill${index}`] && (
                    <EvaluationComment $type="auto">
                      <EvaluationCommentLabel $type="auto">
                        <i className="fa-solid fa-comment"></i>
                        {t("forms.evaluation.labels.employeeComment")}
                      </EvaluationCommentLabel>
                      <EvaluationCommentText $type="auto">
                        {comments[`autoSkill${index}`]}
                      </EvaluationCommentText>
                    </EvaluationComment>
                  )}
                </EvaluationResultCard>
                <EvaluationFormContainer>
                  <EvaluationResultHeader>
                    <EvaluationResultIcon $type="eval">
                      <i className="fa-solid fa-user-pen"></i>
                    </EvaluationResultIcon>
                    <EvaluationResultTitle $type="eval">
                      {t("forms.evaluation.labels.managerEvaluation")}
                    </EvaluationResultTitle>
                  </EvaluationResultHeader>
                  <ObjectiveAutoEvalButton
                    onClick={() => handleShowSkillForm(index)}
                  >
                    <i className="fa-solid fa-pen-to-square" />
                    {t("forms.evaluation.buttons.evaluate")}
                  </ObjectiveAutoEvalButton>
                </EvaluationFormContainer>
              </>
            )}
          </ObjectiveExpandedContent>
        )}
      </CompetencyCard>
    );
  };

  const totalScore = calculateTotalScore();

  // Función para verificar si todos los objetivos y competencias están autoevaluados
  const isAllEvaluated = () => {
    if (!data) return false;

    // Verificar objetivos (1-5)
    const objectives = [
      { criterial: data.criterial01, rating: data.point01 },
      { criterial: data.criterial02, rating: data.point02 },
      { criterial: data.criterial03, rating: data.point03 },
      { criterial: data.criterial04, rating: data.point04 },
      { criterial: data.criterial05, rating: data.point05 },
    ];

    // Verificar que todos los objetivos existentes estén evaluados
    for (const obj of objectives) {
      if (obj.criterial) {
        // Si el rating es null o undefined, no está evaluado
        if (obj.rating === null || obj.rating === undefined) {
          return false;
        }
        // Si es -1, verificar que tenga comentario
        if (obj.rating === -1) {
          const index = objectives.indexOf(obj) + 1;
          const comment = objectiveComments[index];
          if (!comment || comment.trim().length === 0) {
            return false;
          }
        }
      }
    }

    // Verificar competencias
    if (data.professionalSkill && data.professionalSkill.length > 0) {
      for (let i = 0; i < data.professionalSkill.length; i++) {
        // Si el rating es null o undefined, no está evaluado
        if (
          data.professionalSkill[i].points === null ||
          data.professionalSkill[i].points === undefined
        ) {
          return false;
        }
        // Si es -1, verificar que tenga comentario
        if (data.professionalSkill[i].points === -1) {
          const comment = skillComments[i];
          if (!comment || comment.trim().length === 0) {
            return false;
          }
        }
      }
    }

    return true;
  };

  const allEvaluated = isAllEvaluated();

  return (
    <>
      {/* <div className="row justify-content-center mt-5">
        <div className="col-12 col-md-11">
          <div className="row align-items-start">
            <div className="col-12 col-lg-12 mb-3">
              <h2 className="title">
                {t("forms.evaluation.detail")} {data?.year}
              </h2>
              <StatusBadge status={data?.status}>
                {t(status?.label)}
              </StatusBadge>
            </div>
          </div>
        </div>
      </div> */}

      <div className="row justify-content-center mt-5">
        <div className="col-12 col-md-11 marg-btm-1rem marg-top-40px">
          <OrgChartHeader>
            <h3>{t("forms.evaluation.titleEval")}</h3>
            <BreadcrumbDiv>
              <Breadcrumb items={breads} />
            </BreadcrumbDiv>
            {/* <p>{t("forms.consult.orgChart.subtitle")}</p> */}
          </OrgChartHeader>
        </div>
      </div>

      <div className="row justify-content-center ">
        <div className="col-12 col-md-11">
          <InfoCard>
            <UserAvatar>
              {data?.dataUser?.name?.charAt(0)}
              {data?.dataUser?.surname?.charAt(0)}
            </UserAvatar>
            <UserInfo>
              <UserTitle>
                {`${data?.dataUser?.name} ${data?.dataUser?.surname}`}
              </UserTitle>
              <UserName>
                {t(status?.label)}
                <span style={{ marginLeft: "4px" }}>•</span>
                <YearBadge>Evaluación {data?.year}</YearBadge>
              </UserName>
            </UserInfo>
          </InfoCard>
        </div>
      </div>

      {/* <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionTitle>
            <i
              className="fa-solid fa-bullseye"
              style={{ marginRight: "10px" }}
            />
            {t("forms.evaluation.labels.criterialObj")}
          </SectionTitle>
        </div>
      </div> */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionCard>
            <SectionHeader>
              <SectionTitle2>
                <i
                  className="fa-solid fa-bullseye"
                  style={{ marginRight: "10px" }}
                />
                {t("forms.evaluation.labels.criterialObj")}
              </SectionTitle2>
            </SectionHeader>
            <div className="row">
              <div className="col-12">
                {renderObjective(
                  1,
                  data?.criterial01,
                  data?.weightCriterial01,
                  data?.pointAuto01,
                  data?.point01
                )}
                {renderObjective(
                  2,
                  data?.criterial02,
                  data?.weightCriterial02,
                  data?.pointAuto02,
                  data?.point02
                )}
                {renderObjective(
                  3,
                  data?.criterial03,
                  data?.weightCriterial03,
                  data?.pointAuto03,
                  data?.point03
                )}
                {renderObjective(
                  4,
                  data?.criterial04,
                  data?.weightCriterial04,
                  data?.pointAuto04,
                  data?.point04
                )}
                {renderObjective(
                  5,
                  data?.criterial05,
                  data?.weightCriterial05,
                  data?.pointAuto05,
                  data?.point05
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
      <div className="row justify-content-center">
        <div className="col-12 col-md-11"></div>
      </div>

      {/* <div className="row justify-content-center">

        <div className="col-12 col-md-11">
          <SectionTitle>
            <i className="fa-solid fa-star" style={{ marginRight: "10px" }} />
            {t("forms.evaluation.labels.criterialStd")}
          </SectionTitle>
        </div>
      </div> */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <SectionCard>
            <SectionHeader>
              <SectionTitle2>
                <i className="fas fa-award"></i>
                {t("forms.evaluation.labels.professionalCompetenciesTitle")}
              </SectionTitle2>
            </SectionHeader>
            <div className="row">
              <div className="col-12">
                {data?.professionalSkill?.map((skill, index) =>
                  renderCompetency(skill, index)
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="row justify-content-center mt-5 mb-5">
        <div className="col-12 col-md-11 text-center">
          <ActionButton onClick={() => finishEval()} disabled={!allEvaluated}>
            <i
              className="fa-solid fa-pen-to-square"
              style={{ marginRight: "8px" }}
            />
            {t("forms.evaluation.status.evaluated")}
          </ActionButton>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-md-11">
          <div className="mb-5"></div>
        </div>
      </div>
    </>
  );
};

EvaluationManagerFormView.propTypes = {};

export default EvaluationManagerFormView;
